import React, { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Timeline } from '@xzdarcy/react-timeline-editor'
import WaveSurfer from 'wavesurfer.js'
import './index.scss';
import { useInterval } from '@/utils/Hooks';
import i18n from '@/i18n';
import { Trans } from 'react-i18next';
import CanvasVideoPlayer from '../CanvasVideoPlayer';
import { withYoutubeUrlToShortsContext } from '../../contexts/YoutubeUrlToShortsContext';
import UploadCompleted from '@/Common/UploadCompleted/UploadCompleted'
import { HistoryPush } from '@/Interface';
import { ROUTE_PATH } from '@/utils/Global';

const formatDurationHours = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const formatDurationMinutes = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

function CanvasTimelineEditor(props) {
  const {
    context: {
      audioUrl,
      previewImageUrl,
      metadataJson,

      ROW_HEIGHT,
      AUDIO_ROW_HEIGHT,

      timelineData,

      timelineScale,
      currentScaleWidth,

      isPlaying,
      isPaused,
      videoTime,
      videoVolume,
      videoPlaybackSpeed,
      currentTimelineActions,

      timelineRef,

      setTimelineScale,
      setCurrentTimelineActions,

      handlePlayVideo,
      handlePauseVideo,
      handleStopVideo,
      handleMoveToStart,
      handleMoveToEnd,

      updateVideoVolume,
      updatePlaybackSpeed,
      updateVideoCurrentTime,
      updateSentences,

      handleSave,
      handleRendering
    }
  } = props

  // const navigate = useNavigate();
  // const { shortsId } = useParams();

  const { duration } = metadataJson;
  const { sentences } = timelineData;
  const enabledSentences = sentences.filter(sentence => sentence.enabled)
  enabledSentences.sort((a, b) => a.start - b.start)

  const waveformRef = useRef(null);
  const lang = useSelector(state => state.langStore.lang);
  const [isSaving, setIsSaving] = useState(false);
  const [isVolumeHovered, setIsVolumeHovered] = useState(false);
  const [isWaveformLoading, setIsWaveformLoading] = useState(false);
  const [isLoadedWaveform, setIsLoadedWaveform] = useState(false);
  const [isOpenUploadCompletedDialog, setIsOpenUploadCompletedDialog] = useState(false);

  useEffect(() => {
    return () => {
      clearWaveform();
    }
  }, [])

  useInterval(() => {
    try {
      const container = document.getElementById('waveform');
      if (!container || !audioUrl || isWaveformLoading || isLoadedWaveform || waveformRef.current) return;
      setIsWaveformLoading(true);
  
      waveformRef.current = WaveSurfer.create({
        container: container,
        waveColor: "#ffffff",
        progressColor: "#ffffff",
        height: AUDIO_ROW_HEIGHT,
        normalize: true,
        barWidth: 2,
        barRadius: 4,
        barGap: 1,
        backend: 'WebAudio',
        barMinHeight: 4,
        barHeight: 32,
      });
  
      waveformRef.current.on('ready', () => {
        setIsWaveformLoading(false);
        setIsLoadedWaveform(true);
        if (container) {
          container.style.width = `${currentScaleWidth * duration}px`;
          container.style.height = `${AUDIO_ROW_HEIGHT}px`;
          container.style.display = 'block';
        }
      });
  
      waveformRef.current.on('error', (e) => {
        console.error('WaveSurfer error:', e);
        setIsWaveformLoading(false);
      });
  
      waveformRef.current.on('loading', (progress) => {});
  
      waveformRef.current.load(audioUrl);
    }
    catch (e) {
      console.error(e)
    }
  }, isLoadedWaveform ? null : 500)

  const clearWaveform = () => {
    if (waveformRef.current) {
      waveformRef.current.destroy();
      waveformRef.current = null;
    }
  }

  const handleChangeTimelineData = timelineData => {
    const textLayer = (timelineData.find(item => item.type === 'text' && item.actions && item.actions.length > 0))
    if (!textLayer) return;

    updateSentences(textLayer.actions)
  }

  const handleDragTimeline = time => {
    updateVideoCurrentTime(time, { updateTimeline: false })
  }

  const handleClickTimeline = (time, e) => {
    updateVideoCurrentTime(time, { updateTimeline: false })
    return true;
  }

  const handleClickRow = (e, { row, time }) => {
    updateVideoCurrentTime(time, { updateTimeline: false })
  }

  const handleClickAction = (e, { action, row, time }) => {
    if (currentTimelineActions.find(item => item.id === action.id)) {
      setCurrentTimelineActions(currentTimelineActions.filter(item => item.id !== action.id))
    }
    else {
      setCurrentTimelineActions([action])
      // setCurrentTimelineActions([...currentTimelineActions, action])
    }
    updateVideoCurrentTime(time, { updateTimeline: false })
  }

  const handleClickVolumeButton = (e) => {
    e.stopPropagation()
    updateVideoVolume(videoVolume === 0 ? 1 : 0)
  }

  const handleVolumeSliderChange = (e) => {
    e.stopPropagation()
    const newVolume = parseFloat(e.target.value);
    updateVideoVolume(newVolume);
  };

  const handleOpenUploadCompletedDialog = (e) => {
    setIsOpenUploadCompletedDialog(true);
  }

  const handleCloseUploadCompletedDialog = (e) => {
    setIsOpenUploadCompletedDialog(false);
  }

  const handleClickUploadCompletedDialogConfirmButton = (page) => (e) => {
    HistoryPush(ROUTE_PATH.MY_VIDEOS_SHORTS);
    handleCloseUploadCompletedDialog()
  }

  const handleClickSaveButton = async (e) => {
    if (isSaving) return;

    setIsSaving(true)
    try {
      await handleSave()
    }
    catch (e) {}
    setIsSaving(false)
  }

  const handleClickCompleteButton = async (e) => {
    const { result } = await handleRendering()
    if (result) handleOpenUploadCompletedDialog()
    
    // TO-DO : 렌더링 요청 실패 시 안내 메시지 추가
  }

  const renderAction = (action, row) => {
    switch (row.type) {
      case 'video':
        {
          if (!previewImageUrl) return (
            <div className='timeline-editor-action' />
          )

          const {
            url: thumbnailUrl,
            totalFrames
          } = previewImageUrl

          const frames = []
          for (let i = 0; i < totalFrames; i++) {
            frames.push(`${decodeURIComponent(thumbnailUrl).replace('{FRAME}', i + 1)}`)
          }

          let activeClipPath = `polygon(`
          for (let i = 0; i < enabledSentences.length; i++) {
            const { start, end } = enabledSentences[i]
            activeClipPath += `${currentScaleWidth * start}px 0px, ${currentScaleWidth * start}px ${ROW_HEIGHT}px, ${currentScaleWidth * end}px ${ROW_HEIGHT}px, ${currentScaleWidth * end}px 0px, `
          }
          activeClipPath += `0px 0px)`

          return (
            <>
              <div
                className='timeline-editor-action-back-thumbnails'
              >
                {frames.map(src => (
                  <div style={{
                    display: 'inline-block',
                    width: currentScaleWidth * 2,
                    height: ROW_HEIGHT,
                    overflow: 'hidden',
                  }}>
                    <img
                      src={src}
                      alt=''
                      style={{
                        display: 'inline-block',
                        width: currentScaleWidth * 2,
                        height: ROW_HEIGHT,
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                ))}
              </div>
              <div
                className='timeline-editor-action-active-thumbnails'
                style={{ clipPath: activeClipPath }}
              >
                {frames.map(src => (
                  <div style={{
                    display: 'inline-block',
                    width: currentScaleWidth * 2,
                    height: ROW_HEIGHT,
                    overflow: 'hidden',
                  }}>
                    <img
                      draggable={false}
                      src={src}
                      alt=''
                      style={{
                        display: 'inline-block',
                        width: currentScaleWidth * 2,
                        height: ROW_HEIGHT,
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                ))}
              </div>
            </>
          )
        }

      case 'audio':
        return (
          <div className='timeline-editor-action'>
            <div 
              id='waveform' 
              style={{
                width: `${currentScaleWidth * duration}px`,
                height: `${AUDIO_ROW_HEIGHT}px`,
                display: 'block',
              }}
            />
          </div>
        )

      case 'text':
        {
          const isActive = currentTimelineActions.find(item => item.id === action.id)
          return (
            <div className={`timeline-editor-action ${isActive ? 'active' : ''}`}>
              <div className='timeline-editor-action-text'>
                <span>{action.text}</span>
              </div>
  
              {
                isActive && (
                  <>
                    <div className='timeline-editor-action-left-stretch' />
                    <div className='timeline-editor-action-right-stretch' />
                  </>
                )
              }
            </div>
          )
        }
      default:
        break;
    }

    return (
      <div className='timeline-editor-action' />
    )
  }

  const renderScale = scale => {
    return (
      <div>
        {formatDurationMinutes(scale)}
      </div>
    )
  }

  return (
    <div className="canvas-timeline-editor-container">
      <div className="canvas-timeline-editor-top-container">
        <div className="canvas-timeline-top-floating-container">
          <div className="button-wrapper">
            <button className="button" onClick={handleClickSaveButton} disabled={isSaving}>{isSaving ? i18n.t("common.saving.btn") : i18n.t("common.save.btn")}</button>
            <button className="button" onClick={handleClickCompleteButton}>{i18n.t("global.do.complete")}</button>
          </div>
        </div>
        <div className="canvas-timeline-editor-video-container">
          <CanvasVideoPlayer onlyView={true} />
        </div>
      </div>

      <div className="canvas-timeline-editor-bottom-container">
        <div className="canvas-timeline-editor-bottom-timeline-controls">
          <div className="timeline-controls-wrapper">
            <div className="control-group">
              <select defaultValue={videoPlaybackSpeed} onChange={e => updatePlaybackSpeed(Number(e.target.value))}>
                <option value={0.5}>0.5x</option>
                <option value={0.75}>0.75x</option>
                <option value={1}>1.0x</option>
                <option value={1.25}>1.25x</option>
                <option value={1.5}>1.5x</option>
                <option value={1.75}>1.75x</option>
                <option value={2}>2.0x</option>
              </select>
              <label>Speed</label>
            </div>

            <div className="control-group playback-controls">
              <div className="control-button" onClick={handleMoveToStart}>
                <img src="https://front.videomonster.com/images-7cuytg20/common/ai/customize/player-seek-backward.svg" alt="" />
              </div>
              <div className="control-button" onClick={() => {
                if (!isPlaying) {
                  handlePlayVideo()
                }
                else {
                  handlePauseVideo()
                }
              }}>
                {
                  (!isPlaying || isPaused) ?
                    <img src="https://front.videomonster.com/images-7cuytg20/common/ai/customize/player-play.svg" alt="" /> :
                    <img src="https://front.videomonster.com/images-7cuytg20/common/ai/customize/player-pause.svg" alt="" />
                }
              </div>
              <div className="control-button" onClick={handleStopVideo}>
                <img src="https://front.videomonster.com/images-7cuytg20/common/ai/customize/player-stop.svg" alt="" />
              </div>
              <div className="control-time">
                {formatDurationHours(videoTime)}
              </div>
              <div className="control-time-divider">
                /
              </div>
              <div className="control-time">
                {formatDurationHours(duration)}
              </div>
              <div className="control-button" onClick={handleMoveToEnd}>
                <img src="https://front.videomonster.com/images-7cuytg20/common/ai/customize/player-seek-forward.svg" alt="" />
              </div>
            </div>

            <div className="control-group">
              <div className="control-volume-wrapper">
                <div 
                  className="control-volume-button" 
                  onClick={handleClickVolumeButton}
                  onMouseEnter={() => setIsVolumeHovered(true)}
                  onMouseLeave={() => setIsVolumeHovered(false)}
                >
                  {
                    videoVolume === 0 ?
                      <img src="https://front.videomonster.com/images-7cuytg20/common/ai/customize/player-volume-mute.svg" alt="" /> :
                      <img src="https://front.videomonster.com/images-7cuytg20/common/ai/customize/player-volume.svg" alt="" />
                  }
                  {isVolumeHovered && (
                    <div className="volume-slider-container">
                      <div className="volume-slider-inner-container" onClick={e => e.stopPropagation()}>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={videoVolume}
                          onClick={e => e.stopPropagation()}
                          onChange={handleVolumeSliderChange}
                          className="volume-slider"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* <div className="control-scale-wrapper">
                <div className="control-scale-icon">
                  <img src="https://front.videomonster.com/images-7cuytg20/common/ai/customize/timeline-scale-down.svg" alt="" />
                </div>
                <div className='timeline-scale-progressbar'>
                  <div className="timeline-scale-input-range-container">
                    <div
                      className="timeline-scale-input-range-track"
                      style={{
                        left: `0%`,
                        right: `${(100 - (timelineScale - 0.75) / 1 * 100)}%`
                      }}
                    />
                    <input
                      type="range"
                      className="timeline-scale-input-range"
                      min={0.75}
                      max={1.75}
                      step={0.0625}
                      defaultValue={1}
                      value={timelineScale}
                    // onChange={(e) => setTimelineScale(Number(e.target.value))}
                    />
                  </div>
                </div>
                <div className="control-scale-icon">
                  <img src="https://front.videomonster.com/images-7cuytg20/common/ai/customize/timeline-scale-up.svg" alt="" />
                </div>
              </div> */}
            </div>
          </div>
        </div>
        <div className="canvas-timeline-editor-bottom-timeline-editor">
          <div className="timeline-editor-left-tabs">
            <div className="timeline-editor-left-tab video">
              <span>Video</span>
            </div>
            <div className="timeline-editor-left-tab audio">
              <span>Audio</span>
            </div>
            {/* <div className="timeline-editor-left-tab txt">
              <span>TXT</span>
            </div>
            <div className="timeline-editor-left-tab bgm">
              <span>BGM</span>
            </div> */}
          </div>
          <div className="timeline-editor-wrapper">
            <Timeline
              scale={timelineScale}
              scaleWidth={currentScaleWidth}
              ref={timelineRef}
              onChange={handleChangeTimelineData}
              getActionRender={renderAction}
              getScaleRender={renderScale}
              onCursorDrag={handleDragTimeline}
              onCursorDragEnd={handleDragTimeline}
              onClickTimeArea={handleClickTimeline}
              gridSnap={true}
              gridSnapTolerance={10}
              dragSnap={true}
              autoScroll={true}
              onClickRow={handleClickRow}
              onClickAction={handleClickAction}
              editorData={[
                {
                  id: 'video-layer',
                  type: 'video',
                  rowHeight: ROW_HEIGHT,
                  classNames: ['timeline-video'],
                  actions: [
                    {
                      id: 'video-layer',
                      start: 0,
                      end: duration,
                      flexible: false,
                      movable: false
                    }
                  ]
                },
                {
                  id: 'audio-layer',
                  type: 'audio',
                  rowHeight: AUDIO_ROW_HEIGHT,
                  classNames: ['timeline-audio'],
                  actions: [
                    {
                      id: 'audio-layer',
                      start: 0,
                      end: duration,
                      flexible: false,
                      movable: false
                    }
                  ]
                },
                {
                  id: 'text-layer',
                  type: 'text',
                  rowHeight: ROW_HEIGHT,
                  classNames: ['timeline-text'],
                  actions: enabledSentences.map((sentence, i) => {
                    let minStart = 0
                    let maxEnd = duration

                    if (i > 0) {
                      minStart = enabledSentences[i - 1].end
                    }
                    if (i < enabledSentences.length - 1) {
                      maxEnd = enabledSentences[i + 1].start
                    }

                    return {
                      ...sentence,
                      minStart,
                      maxEnd,
                      flexible: true,
                      movable: true,
                    }
                  })
                }
              ]}
            />
          </div>
        </div>
      </div>


      <UploadCompleted
        hide={true}
        fullWidth={true}
        dialogHeadText={i18n.t("makevideo.paletteitem.UploadCompleted.dialogHeadText1")}
        dialogBodyText1={
          <span>
            <Trans i18nKey="makevideo.paletteitem.UploadCompleted.dialogBodyText4">
              <span style={{ color: "#1fbdcb" }} />
            </Trans>
          </span>
        }
        DesignedButtonText={i18n.t("makevideo.paletteitem.UploadCompleted.DesignedButtonText1")}
        open={isOpenUploadCompletedDialog}
        closeUploadCompletedDialog={handleClickUploadCompletedDialogConfirmButton}
        closeBtn={handleCloseUploadCompletedDialog}
        left={lang === "ko" ? 20 : 75}
        width={lang === "ko" ? "277px" : "300px"}
      />
    </div>
  );
}

export default withYoutubeUrlToShortsContext(CanvasTimelineEditor);